import { type CSSProperties, useCallback, useMemo, useRef } from 'react';

import classNames from 'classnames';

import { getBackgroundColorStyle } from '../../../utils/background';
import { cn } from '../../../utils/cn';
import { computeColor } from '../../../utils/colors';
import { computeJustifyContent } from '../../../utils/computeAlignment';
import { computeFont } from '../../../utils/fonts';
import { getFontSizeVariables } from '../../../utils/getFontSizeVariables';

import { ButtonIcon } from '../../..';
import * as styles from '../../Navbar.css';
import {
  NAVBAR_MENU_ITEM_BORDER_DEFAULTS,
  NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2,
  NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2,
} from '../NavbarMenuItem.constants';
import type {
  NavbarMenuItemV2ButtonElementProps,
  NavbarMenuItemV2LinkElementProps,
  TNavbarItemV2ButtonElement,
} from '../NavbarMenuItem.type';

export function NavbarMenuItemLinkV2(
  props: (NavbarMenuItemV2LinkElementProps | NavbarMenuItemV2ButtonElementProps) & { isActive?: boolean },
) {
  // remove excludedSeparator from rest props since we're spreading it on the a tag (which is frowned upon)
  const {
    element,
    className,
    children,
    onClick,
    attributes,
    excludeSeparator: _excludeSeparator,
    isActive,
    ...rest
  } = props;

  const {
    type,
    href,
    isCustomLink,
    label,
    new_tab,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
    hoverUnderline,
    activeTextColor,
    activeUnderline,
    activeFontWeight,
    mobileColor,
    mobileFontSize,
    mobileBackground,
    mobilePadding,
    burgerPadding,
    mobileBorderRadius,
    mobileBorderColor,
    mobileBorderWidth,
    mobileBorderStyle,
    mobileWidth,
    mobileJustify,
  } = element.attrs || {};

  const {
    icon = undefined,
    iconPlacement = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.iconPlacement,
    iconColor = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.iconColor,
    iconSize = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.iconSize,
    hoverIconColor = iconColor,
  } = element.attrs?.type === 'button' ? element.attrs || {} : {};

  const { fontFamily, fontSize, fontWeight } = element.attrs || {};
  // Type cast only to fix type error
  const { letterSpacing } = (element.attrs as TNavbarItemV2ButtonElement['attrs']) || {};
  const elementRef = useRef<HTMLDivElement>(null);
  const isInsideBurgerContent = !!elementRef?.current
    ?.closest('.navbar-menu-burger-content')
    ?.contains(elementRef.current);
  const mobileWidthValue = isInsideBurgerContent && !mobileWidth ? '100%' : mobileWidth;

  const visible = element.attrs?.visible ?? true;

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onClick) return;
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  if (element.attrs?.type !== 'link' && element.attrs?.type !== 'button') {
    return null;
  }

  if (!visible) {
    return null;
  }

  const DEFAULTS = type === 'button' ? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2 : NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2;
  const classStyle = type === 'button' ? styles.navbarItemBaseV2 : styles.navbarItemLinkV2;
  const defaultPadding =
    type === 'button' ? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.padding : NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding;

  const buttonStyles: CSSProperties = {
    '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
    '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
    '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
    '--background-color': getBackgroundColorStyle(background ?? DEFAULTS.background).background,
    '--mobile-background-color': getBackgroundColorStyle(mobileBackground || background || DEFAULTS.background)
      .background,
    '--mobile-border-color':
      mobileBorderColor || borderColor || hoverBorderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
    '--mobile-border-width':
      mobileBorderWidth || borderWidth || hoverBorderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
    '--mobile-border-style':
      mobileBorderStyle || borderStyle || hoverBorderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
    '--mobile-border-radius':
      mobileBorderRadius || borderRadius || hoverBorderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
  } as CSSProperties;

  const iconRightStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
  } as CSSProperties;

  // When serialized (main editor, Orchid), `label` can be stale after inline text edits.
  // Inspect the text container's content to detect the empty state. Falls back to `label`
  // in the TipTap editor path where `element.content` isn't provided.
  const textContainer = element?.content?.find((c) => c.type === 'navbar_item_text_container');
  const textContainerHasText = textContainer
    ? Boolean(textContainer.content?.some((n) => n.type === 'text' && typeof n.text === 'string' && n.text.length > 0))
    : null;
  const isNoText = textContainerHasText === null ? !label || label?.length === 0 : !textContainerHasText;

  return (
    <div
      className={classNames(styles.navbarItemLinkContainerV2)}
      {...attributes}
      ref={elementRef}
      style={
        {
          '--mobile-width': mobileWidthValue,
          '--mobile-item-alignment': computeJustifyContent(mobileJustify),
        } as CSSProperties
      }
    >
      {/* spreading props on standard DOM elements causes React warnings and hydration errors */}
      <a
        {...rest}
        {...customAttributes}
        onClick={handleClick}
        className={classNames(
          classStyle,
          className,
          customAttributes?.className,
          type === 'button' && styles.navbarItemMobileBaseV2,
        )}
        style={
          {
            ...(type === 'button' ? buttonStyles : {}),
            '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
            '--padding': padding ?? defaultPadding,
            '--text-color': computeColor(color ?? DEFAULTS.color),
            '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
            ...getFontSizeVariables(fontSize ?? DEFAULTS.fontSize, mobileFontSize),
            '--font-weight': fontWeight ?? DEFAULTS.fontWeight,
            '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,

            '--border-width': hoverBorderWidth,
            '--hover-border-radius': hoverBorderRadius || borderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
            '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,

            '--hover-underline': hoverUnderline ? 'underline' : 'none',

            '--active-text-color': activeTextColor ? computeColor(activeTextColor) : undefined,
            '--active-underline': activeUnderline ? 'underline' : undefined,
            '--active-font-weight': activeFontWeight,

            '--mobile-text-color': computeColor(mobileColor || color || DEFAULTS.color),
            '--mobile-padding': mobilePadding || padding || DEFAULTS.padding,
            '--burger-item-padding': burgerPadding || undefined,
            ...(type === 'button' && icon && iconPlacement === 'right' && iconRightStyles),
          } as CSSProperties
        }
        href={cleanHref}
        target={new_tab ? '_blank' : '_self'}
        data-active={isActive || undefined}
        aria-current={isActive ? 'page' : undefined}
      >
        {icon && iconPlacement === 'left' ? (
          <ButtonIcon
            icon={icon}
            color={iconColor}
            hoverColor={hoverIconColor}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.iconV2)}
            size={iconSize}
          />
        ) : null}
        {children}
        {icon && iconPlacement === 'right' ? (
          <ButtonIcon
            color={iconColor}
            hoverColor={hoverIconColor}
            icon={icon}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.iconV2)}
            size={iconSize}
          />
        ) : null}
      </a>
      <div className={styles.navbarItemSeparatorV2} />
    </div>
  );
}
